import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { FaEdit, FaTrash, FaPlus, FaTimes, FaLink } from 'react-icons/fa';

const emptyForm = { question: '', answer: '', keywords: '', links: [{ label: '', url: '' }] };

const ChatbotQATab = ({ auth }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const getAuthConfig = () => {
    const config = { headers: { 'Content-Type': 'application/json' } };
    if (auth.token) config.headers['Authorization'] = 'Bearer ' + auth.token;
    return config;
  };

  const fetchItems = async () => {
    try {
      const res = await axios.get('/api/chatbot-qa');
      setItems(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const openNew = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setForm({
      question: item.question,
      answer: item.answer,
      keywords: item.keywords.join(', '),
      links: item.links.length > 0 ? item.links : [{ label: '', url: '' }]
    });
    setEditingId(item._id);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        question: form.question,
        answer: form.answer,
        keywords: form.keywords.split(',').map(k => k.trim().toLowerCase()).filter(Boolean),
        links: form.links.filter(l => l.label && l.url)
      };

      if (editingId) {
        await axios.put(`/api/chatbot-qa/${editingId}`, payload, getAuthConfig());
      } else {
        await axios.post('/api/chatbot-qa', payload, getAuthConfig());
      }
      closeForm();
      fetchItems();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this Q&A?')) return;
    try {
      await axios.delete(`/api/chatbot-qa/${id}`, getAuthConfig());
      fetchItems();
    } catch (err) {
      console.error(err);
    }
  };

  const addLink = () => {
    setForm({ ...form, links: [...form.links, { label: '', url: '' }] });
  };

  const removeLink = (index) => {
    setForm({ ...form, links: form.links.filter((_, i) => i !== index) });
  };

  const updateLink = (index, field, value) => {
    const updated = form.links.map((l, i) => i === index ? { ...l, [field]: value } : l);
    setForm({ ...form, links: updated });
  };

  if (loading) {
    return <div className="text-foreground/60 p-4">Loading...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-foreground">Chatbot Q&A</h2>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#0d6e87] transition-colors text-sm font-medium"
        >
          <FaPlus size={12} /> Add Q&A
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-card rounded-lg shadow-lg w-full max-w-lg p-6 border border-border max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-foreground">
                {editingId ? 'Edit Q&A' : 'Add Q&A'}
              </h3>
              <button onClick={closeForm} className="text-foreground/40 hover:text-foreground/60">
                <FaTimes size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Question</label>
                <input
                  type="text"
                  value={form.question}
                  onChange={(e) => setForm({ ...form, question: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-[#1086a4]"
                  placeholder="e.g. How do I submit an assignment?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Answer</label>
                <textarea
                  value={form.answer}
                  onChange={(e) => setForm({ ...form, answer: e.target.value })}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-[#1086a4]"
                  placeholder="The answer students will see..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Keywords <span className="text-foreground/40 font-normal">(comma separated)</span>
                </label>
                <input
                  type="text"
                  value={form.keywords}
                  onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-[#1086a4]"
                  placeholder="e.g. submit, assignment, upload, hand in"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Links <span className="text-foreground/40 font-normal">(optional)</span>
                </label>
                {form.links.map((link, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={link.label}
                      onChange={(e) => updateLink(i, 'label', e.target.value)}
                      className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-[#1086a4]"
                      placeholder="Link label"
                    />
                    <input
                      type="text"
                      value={link.url}
                      onChange={(e) => updateLink(i, 'url', e.target.value)}
                      className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-[#1086a4]"
                      placeholder="/courses or https://..."
                    />
                    {form.links.length > 1 && (
                      <button type="button" onClick={() => removeLink(i)} className="text-red-400 hover:text-red-600 px-1">
                        <FaTimes size={14} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addLink}
                  className="text-[#1086a4] text-sm hover:underline flex items-center gap-1"
                >
                  <FaPlus size={10} /> Add another link
                </button>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 border border-[#1086a4] rounded-lg text-[#1086a4] font-medium hover:bg-[#f9fafb] transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-[#1086a4] text-white rounded-lg font-medium hover:bg-[#0d6e87] disabled:opacity-50 transition-colors text-sm"
                >
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Q&A List */}
      {items.length === 0 ? (
        <div className="text-center py-12 text-foreground/50">
          <p className="text-lg mb-2">No Q&A entries yet</p>
          <p className="text-sm">Add questions and answers that students can ask the chatbot.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item._id} className="border border-border rounded-lg p-4 bg-background">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground text-sm">{item.question}</h4>
                  <p className="text-foreground/60 text-sm mt-1">{item.answer}</p>
                  {item.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {item.keywords.map((kw, i) => (
                        <span key={i} className="px-2 py-0.5 bg-muted text-foreground/60 rounded-full text-xs">
                          {kw}
                        </span>
                      ))}
                    </div>
                  )}
                  {item.links.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {item.links.map((link, i) => (
                        <span key={i} className="flex items-center gap-1 text-[#1086a4] text-xs">
                          <FaLink size={10} /> {link.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => openEdit(item)} className="text-foreground/40 hover:text-[#1086a4] transition-colors">
                    <FaEdit size={14} />
                  </button>
                  <button onClick={() => handleDelete(item._id)} className="text-foreground/40 hover:text-red-500 transition-colors">
                    <FaTrash size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({ auth: state.auth });
export default connect(mapStateToProps)(ChatbotQATab);
